import { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Timer, Check, Carrot, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWakeLock } from "@/hooks/useWakeLock";

const fmtQty = (q) => {
  if (q === null || q === undefined || q === "") return "";
  const r = Math.round(q * 100) / 100;
  return Number.isInteger(r) ? r : r.toFixed(2).replace(/\.?0+$/, "");
};

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; o.type = "sine";
    g.gain.setValueAtTime(0.25, ctx.currentTime);
    o.start();
    let n = 0;
    const iv = setInterval(() => { n += 1; g.gain.value = n % 2 ? 0 : 0.25; }, 250);
    setTimeout(() => { clearInterval(iv); o.stop(); ctx.close(); }, 1600);
  } catch (e) { /* audio indispo */ }
}

// Détecte les durées dans un texte : "10 min", "1 h", "2 heures", "1h30"
function detectDurations(text) {
  const res = [];
  const re = /(\d+)\s*(h|heures?)\s*(\d+)?|(\d+)\s*(min|minutes?)/gi;
  let m;
  while ((m = re.exec(text || ""))) {
    let sec = 0;
    if (m[1]) sec = parseInt(m[1], 10) * 3600 + (m[3] ? parseInt(m[3], 10) * 60 : 0);
    else if (m[4]) sec = parseInt(m[4], 10) * 60;
    if (sec > 0 && sec <= 6 * 3600) res.push(sec);
  }
  return [...new Set(res)].slice(0, 3);
}

const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const fmtDur = (s) => (s >= 3600 ? `${Math.floor(s / 3600)}h${s % 3600 ? String(Math.floor((s % 3600) / 60)).padStart(2, "0") : ""}` : `${Math.floor(s / 60)} min`);

export default function CookingMode({ recipe, servings, onClose }) {
  useWakeLock(true);
  const steps = (recipe.steps || []).slice().sort((a, b) => (a.number || 0) - (b.number || 0));
  const [i, setI] = useState(0);
  const [showIng, setShowIng] = useState(false);
  const [checked, setChecked] = useState({});
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  const total = steps.length;
  const step = steps[i];
  const factor = recipe.servings ? (servings || recipe.servings) / recipe.servings : 1;
  const ingredients = recipe.ingredients || [];
  const durations = useMemo(() => detectDurations(step?.instructions), [step]);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(ref.current); setRunning(false); beep(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const startTimer = (sec) => { setSeconds(sec); setRunning(true); };
  const toggleCheck = (idx) => setChecked((c) => ({ ...c, [idx]: !c[idx] }));

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto" style={{ background: "var(--at-bg)" }} data-testid="cooking-mode">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/5 px-5 py-3 backdrop-blur-md" style={{ background: "rgba(251,249,245,0.9)" }}>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--at-brand)" }}>Mode cuisine</p>
          <p className="truncate font-display text-lg font-semibold">{recipe.title}</p>
        </div>
        <button data-testid="close-cooking-btn" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white at-shadow at-press"><X size={20} /></button>
      </div>

      <div className="mx-auto w-full max-w-2xl flex-1 px-5 py-5">
        {total === 0 ? (
          <div className="mt-20 text-center" style={{ color: "var(--at-muted)" }}>Cette recette n'a pas d'étapes.</div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-center gap-1.5">
              {steps.map((_, idx) => (
                <span key={idx} className="h-2 rounded-full transition-all" style={{ width: idx === i ? 24 : 8, background: idx <= i ? "var(--at-brand)" : "var(--at-border)" }} />
              ))}
            </div>

            <div key={i} className="at-fade-up rounded-3xl border border-black/5 bg-white p-6 at-shadow-lg">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold text-white" style={{ background: "var(--at-brand)" }}>{i + 1}</span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Étape {i + 1} / {total}</p>
                  {step.title && <p className="font-display text-xl font-semibold leading-tight">{step.title}</p>}
                </div>
              </div>
              <p className="text-lg leading-relaxed" style={{ color: "var(--at-text)" }}>{step.instructions}</p>

              {/* Minuteur auto détecté */}
              {durations.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="detected-timers">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Minuteur :</span>
                  {durations.map((sec) => (
                    <button key={sec} data-testid={`start-detected-${sec}`} onClick={() => startTimer(sec)}
                      className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-white at-press" style={{ background: "var(--at-sage)" }}>
                      <Play size={13} /> {fmtDur(sec)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Ingrédients cochables (quantités ajustées) */}
            {ingredients.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-2xl border border-black/5 bg-white at-shadow" data-testid="cooking-ingredients">
                <button onClick={() => setShowIng((v) => !v)} data-testid="toggle-ingredients-btn" className="flex w-full items-center justify-between px-4 py-3 at-press">
                  <span className="flex items-center gap-2 font-semibold">
                    <Carrot size={18} style={{ color: "var(--at-sage)" }} /> Ingrédients
                    <span className="text-xs font-normal" style={{ color: "var(--at-muted)" }}>({servings || recipe.servings} pers.)</span>
                  </span>
                  <ChevronDown size={18} style={{ transform: showIng ? "rotate(180deg)" : "none", color: "var(--at-muted)" }} />
                </button>
                {showIng && (
                  <div className="divide-y divide-black/5 px-4 pb-2">
                    {ingredients.map((ing, idx) => (
                      <button key={idx} onClick={() => toggleCheck(idx)} data-testid={`cook-ing-${idx}`} className="flex w-full items-center gap-3 py-2.5 text-left at-press">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${checked[idx] ? "text-white" : ""}`}
                          style={checked[idx] ? { background: "var(--at-sage)", borderColor: "var(--at-sage)" } : { borderColor: "var(--at-border)" }}>
                          {checked[idx] && <Check size={14} />}
                        </span>
                        <span className={`flex-1 text-sm capitalize ${checked[idx] ? "line-through opacity-50" : ""}`} style={{ color: "var(--at-text-2)" }}>{ing.normalized_name || ing.original_name}</span>
                        <span className={`text-sm font-semibold ${checked[idx] ? "opacity-50" : ""}`} style={{ color: "var(--at-brand)" }}>
                          {ing.quantity !== null && ing.quantity !== undefined ? `${fmtQty(ing.quantity * factor)} ${ing.unit || ""}`.trim() : (ing.raw_quantity || ing.unit || "—")}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex gap-3">
              <Button data-testid="prev-step-btn" variant="outline" disabled={i === 0} onClick={() => setI((x) => Math.max(0, x - 1))} className="h-13 flex-1 rounded-2xl py-3">
                <ChevronLeft size={20} className="mr-1" /> Précédent
              </Button>
              {i < total - 1 ? (
                <Button data-testid="next-step-btn" onClick={() => setI((x) => Math.min(total - 1, x + 1))} className="h-13 flex-1 rounded-2xl py-3 font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
                  Suivant <ChevronRight size={20} className="ml-1" />
                </Button>
              ) : (
                <Button data-testid="finish-cooking-btn" onClick={onClose} className="h-13 flex-1 rounded-2xl py-3 font-semibold text-white at-press" style={{ background: "var(--at-sage)" }}>
                  <Check size={20} className="mr-1.5" /> Terminé
                </Button>
              )}
            </div>

            {/* Minuteur */}
            <div className="mt-5 rounded-3xl border border-black/5 bg-white p-4 at-shadow" data-testid="cook-timer">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>
                <Timer size={14} /> Minuteur
              </div>
              <div className="mb-3 text-center font-display text-5xl font-bold tabular-nums" data-testid="timer-display"
                style={{ color: seconds > 0 && seconds <= 10 && running ? "var(--at-brand)" : "var(--at-text)" }}>
                {fmtClock(seconds)}
              </div>
              <div className="mb-3 grid grid-cols-4 gap-2">
                {[1, 5, 10, 15].map((m) => (
                  <button key={m} data-testid={`timer-add-${m}`} onClick={() => setSeconds((s) => s + m * 60)} className="rounded-xl border border-black/10 py-2 text-sm font-medium at-press">+{m}m</button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button data-testid="timer-toggle" onClick={() => seconds > 0 && setRunning((r) => !r)} disabled={seconds === 0} className="h-11 flex-1 rounded-xl font-semibold text-white at-press" style={{ background: "var(--at-sage)" }}>
                  {running ? <><Pause size={18} className="mr-1.5" /> Pause</> : <><Play size={18} className="mr-1.5" /> Démarrer</>}
                </Button>
                <Button data-testid="timer-reset" onClick={() => { setRunning(false); setSeconds(0); }} variant="outline" className="h-11 rounded-xl px-4">
                  <RotateCcw size={18} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
